/**
 * @fileoverview The See them page (E19, /what-to-plant/browse; design.md 3 and mock-ups b, c): every crop that can go in
 * now at the garden's place that she has none of, so a gardener with few or no plants can add one in two taps.
 *
 * Drawn from the server's answer (services/plants/whatToPlantBrowse browseFor): the back link to What to plant, the
 * heading, the place and count line, the one-sentence promise, the search box, then the kinds (Vegetables · 10, Herbs,
 * Fruit, Flowers) with their rows, A to Z, "+ Add" on each; the line naming the kinds with nothing open; at a count of
 * zero the server's one sentence instead. Typing replaces the kinds with the search's results (CropSearch), the same
 * drawing as on What to plant. "+ Add" opens the Add sheet (AddCropSheet, rule 7); once added the row turns to tick +
 * Added, Open it ›, stays where it is, and the sub line is the server's count worked out again (useAddCrop).
 *
 * The What to plant tab stays lit (TabBar matches the path's start). Free and paused see the server's locked line.
 * Loading: the skeleton rows and "Finding what's open where you are"; a failure: ServerFailureNote (reported, the
 * support address, no retry words).
 *
 * The page may open on a search: ?q=blueberry fills the box (a link, a test); typing does not change the address.
 *
 * Where: the route /what-to-plant/browse (AppRoutes), from What to plant's "See them ›".
 * API: GET /plants/what-to-plant/browse (useWhatToPlantBrowse), GET /plants/what-to-plant/search (CropSearch, E24),
 * POST /plants/what-to-plant/add (useAddCrop).
 * Words (en/es): whatToPlant.back, whatToPlant.loading; the rest are the server's.
 *
 * @module WhatToPlantBrowsePage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useWhatToPlantBrowse } from './useWhatToPlantBrowse';
import { useAddCrop } from './useAddCrop';
import { CropRows } from './CropRows';
import { CropSearchBox, CropSearchResults } from './CropSearch';
import { AddCropSheet } from './AddCropSheet';

/**
 * The page.
 *
 * @returns The See them page.
 */
export const WhatToPlantBrowsePage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { data, isLoading, error } = useWhatToPlantBrowse();
  const adder = useAddCrop();
  const [params] = useSearchParams();
  // A link may open the page on a search (?q=blueberry); typing changes only what is drawn, not the address.
  const [query, setQuery] = React.useState(() => params.get('q') ?? '');
  // After an add, the server's count worked out again.
  const lastSub = adder.lastKey !== null ? adder.added.get(adder.lastKey)?.sub ?? null : null;
  const picker = data?.picker;

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/what-to-plant"
        className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        {t('whatToPlant.back')}
      </Link>
      {data?.heading && <h1 className="font-grow text-2xl font-bold text-foreground">{data.heading}</h1>}

      {error ? (
        <ServerFailureNote error={error} where="whatToPlantBrowse" className="mt-4" />
      ) : isLoading || !data ? (
        <div className="mt-2" aria-busy="true">
          <p className="text-sm text-muted-foreground">{t('whatToPlant.loading')}</p>
          <div className="mt-3 space-y-2">
            {[0, 1, 2, 3, 4].map((n) => <div key={n} className="h-14 animate-pulse rounded-lg bg-muted" />)}
          </div>
        </div>
      ) : data.notice || !picker ? (
        data.notice && (
          <div className="mt-4 rounded-lg border bg-card p-4">
            <p className="text-sm text-foreground">{data.notice.line}</p>
          </div>
        )
      ) : (
        <>
          <p className="mt-0.5 text-sm text-muted-foreground">{lastSub ?? data.sub}</p>
          <p className="mt-2 text-sm text-muted-foreground">{data.promise}</p>
          <CropSearchBox query={query} onChange={setQuery} />
          {query.trim() ? (
            <CropSearchResults query={query} adder={adder} addedWords={picker.added} />
          ) : (
            <>
              {(data.kinds ?? []).map((kind) => (
                <section key={kind.key} className="mt-5" aria-label={kind.heading}>
                  <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {kind.heading}
                    <span className="font-normal">{` · ${kind.count}`}</span>
                  </h2>
                  <CropRows rows={kind.rows} adder={adder} addedWords={picker.added} />
                </section>
              ))}
              {data.emptyKinds && <p className="mt-5 text-sm text-muted-foreground">{data.emptyKinds}</p>}
              {data.none && <p className="mt-5 text-sm text-muted-foreground">{data.none}</p>}
            </>
          )}
          <AddCropSheet adder={adder} words={picker.sheet} />
        </>
      )}
    </div>
  );
};
